/**
 * Firebase Authentication Service - Options Oracle V2
 * Handles Google Sign-in, Email Auth, and User Data Management
 */
import {
    createUserWithEmailAndPassword,
    signOut as firebaseSignOut,
    getRedirectResult,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    type User,
    updateProfile,
} from 'firebase/auth'
import {
    collection,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    serverTimestamp,
    setDoc,
    type Timestamp,
    updateDoc,
} from 'firebase/firestore'
import { getFirebaseAuth, getFirestore, getGoogleProvider } from './firebase'

// User data interface
export interface UserData {
    uid: string
    email: string
    displayName: string
    photoURL: string | null
    role: 'user' | 'admin'
    status: 'pending' | 'approved' | 'rejected'
    provider: 'google' | 'email'
    createdAt: Timestamp
    lastLogin: Timestamp
}

// Admin email for auto-approval
const ADMIN_EMAIL = 'bathinimallikarjuna637@gmail.com'

/**
 * Process signed-in user - Create/update Firestore document
 */
const processSignInUser = async (user: User): Promise<UserData> => {
    const db = getFirestore()
    const userDocRef = doc(db, 'users', user.uid)
    const userDoc = await getDoc(userDocRef)

    let userData: UserData

    if (!userDoc.exists()) {
        // Create new user document
        const isAdminEmail = user.email === ADMIN_EMAIL

        userData = {
            uid: user.uid,
            email: user.email || '',
            displayName: user.displayName || '',
            photoURL: user.photoURL,
            role: isAdminEmail ? 'admin' : 'user',
            status: isAdminEmail ? 'approved' : 'pending',
            provider: 'google',
            createdAt: serverTimestamp() as Timestamp,
            lastLogin: serverTimestamp() as Timestamp,
        }
        await setDoc(userDocRef, userData)
    } else {
        // Update last login
        await setDoc(userDocRef, { lastLogin: serverTimestamp() }, { merge: true })
        userData = userDoc.data() as UserData
    }

    return userData
}

/**
 * Sign in with Google (Popup with Redirect fallback)
 */
export const signInWithGoogle = async () => {
    try {
        const auth = getFirebaseAuth()
        const provider = getGoogleProvider()
        const result = await signInWithPopup(auth, provider)
        const userData = await processSignInUser(result.user)

        return {
            success: true,
            user: result.user,
            userData: userData,
            pending: false,
        }
    } catch (error: unknown) {
        const err = error as { code?: string; message?: string }

        // Fallback to redirect if popup fails
        if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
            console.warn('[Auth] Popup blocked, switching to redirect...')
            const auth = getFirebaseAuth()
            const provider = getGoogleProvider()
            await signInWithRedirect(auth, provider)
            return {
                success: true,
                pending: true,
                user: null,
                userData: null,
            }
        }

        console.error('[Auth] Google Sign-In Error:', error)
        return {
            success: false,
            error: err.message || 'Sign-in failed',
        }
    }
}

/**
 * Handle redirect result (call on app mount)
 */
export const completeGoogleSignIn = async () => {
    try {
        const auth = getFirebaseAuth()
        const result = await getRedirectResult(auth)
        if (result) {
            const userData = await processSignInUser(result.user)
            return {
                success: true,
                user: result.user,
                userData: userData,
            }
        }
        return null
    } catch (error: unknown) {
        const err = error as { message?: string }
        console.error('[Auth] Redirect result error:', error)
        return {
            success: false,
            error: err.message || 'Redirect failed',
        }
    }
}

/**
 * Sign in with Email and Password
 */
export const signInWithEmail = async (email: string, password: string) => {
    try {
        const auth = getFirebaseAuth()
        const db = getFirestore()
        const result = await signInWithEmailAndPassword(auth, email, password)
        const user = result.user

        // Update last login
        const userDocRef = doc(db, 'users', user.uid)
        await setDoc(userDocRef, { lastLogin: serverTimestamp() }, { merge: true })

        const userData = await getUserData(user.uid)

        return {
            success: true,
            user: user,
            userData: userData,
        }
    } catch (error: unknown) {
        const err = error as { message?: string }
        console.error('[Auth] Email sign-in error:', error)
        return {
            success: false,
            error: err.message || 'Sign-in failed',
        }
    }
}

/**
 * Sign up with Email and Password
 */
export const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    try {
        const auth = getFirebaseAuth()
        const db = getFirestore()
        const result = await createUserWithEmailAndPassword(auth, email, password)
        const user = result.user

        await updateProfile(user, { displayName })

        const isAdminEmail = email === ADMIN_EMAIL

        const userData: UserData = {
            uid: user.uid,
            email: email,
            displayName: displayName,
            photoURL: null,
            role: isAdminEmail ? 'admin' : 'user',
            status: isAdminEmail ? 'approved' : 'pending',
            provider: 'email',
            createdAt: serverTimestamp() as Timestamp,
            lastLogin: serverTimestamp() as Timestamp,
        }

        const userDocRef = doc(db, 'users', user.uid)
        await setDoc(userDocRef, userData)

        return {
            success: true,
            user: user,
            userData: userData,
        }
    } catch (error: unknown) {
        const err = error as { message?: string }
        console.error('[Auth] Email sign-up error:', error)
        return {
            success: false,
            error: err.message || 'Sign-up failed',
        }
    }
}

/**
 * Sign out
 */
export const signOut = async () => {
    try {
        const auth = getFirebaseAuth()
        await firebaseSignOut(auth)
        return { success: true }
    } catch (error: unknown) {
        const err = error as { message?: string }
        console.error('[Auth] Sign-out error:', error)
        return {
            success: false,
            error: err.message || 'Sign-out failed',
        }
    }
}

/**
 * Get user data from Firestore
 */
export const getUserData = async (uid: string): Promise<UserData | null> => {
    try {
        const db = getFirestore()
        const userDocRef = doc(db, 'users', uid)
        const userDoc = await getDoc(userDocRef)

        if (userDoc.exists()) {
            const data = userDoc.data() as UserData

            // Force admin role for the admin email
            if (data.email === ADMIN_EMAIL) {
                return { ...data, role: 'admin', status: 'approved' }
            }
            return data
        }
        return null
    } catch (error) {
        console.error('[Auth] Error fetching user data:', error)
        return null
    }
}

/**
 * Auth state observer
 */
export const onAuthChange = (callback: (user: User | null) => void) => {
    const auth = getFirebaseAuth()
    return onAuthStateChanged(auth, callback)
}

/**
 * Real-time user data subscription
 */
export const onUserDataChange = (uid: string, callback: (data: UserData | null) => void) => {
    const db = getFirestore()
    const userDocRef = doc(db, 'users', uid)

    return onSnapshot(
        userDocRef,
        (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data() as UserData
                if (data.email === ADMIN_EMAIL) {
                    callback({ ...data, role: 'admin', status: 'approved' })
                } else {
                    callback(data)
                }
            } else {
                callback(null)
            }
        },
        (error) => {
            console.error('[Auth] Real-time user data error:', error)
            callback(null)
        },
    )
}

/**
 * Get all users (Admin only)
 */
export const getAllUsers = async (): Promise<UserData[]> => {
    try {
        const db = getFirestore()
        const usersRef = collection(db, 'users')
        const snapshot = await getDocs(usersRef)
        return snapshot.docs.map((doc) => doc.data() as UserData)
    } catch (error) {
        console.error('[Auth] Error fetching all users:', error)
        return []
    }
}

/**
 * Update user status (Admin only)
 */
export const updateUserStatus = async (uid: string, status: 'pending' | 'approved' | 'rejected'): Promise<boolean> => {
    try {
        const db = getFirestore()
        const userDocRef = doc(db, 'users', uid)
        await updateDoc(userDocRef, { status })
        return true
    } catch (error) {
        console.error('[Auth] Error updating user status:', error)
        return false
    }
}

/**
 * Update user role (Admin only)
 */
export const updateUserRole = async (uid: string, role: 'user' | 'admin'): Promise<boolean> => {
    try {
        const db = getFirestore()
        const userDocRef = doc(db, 'users', uid)
        await updateDoc(userDocRef, { role })
        return true
    } catch (error) {
        console.error('[Auth] Error updating user role:', error)
        return false
    }
}

/**
 * Subscribe to all users (Real-time for Admin)
 */
export const onAllUsersChange = (callback: (users: UserData[]) => void) => {
    const db = getFirestore()
    const usersRef = collection(db, 'users')

    return onSnapshot(
        usersRef,
        (snapshot) => {
            const users = snapshot.docs.map((doc) => doc.data() as UserData)
            callback(users)
        },
        (error) => {
            console.error('[Auth] Real-time all users error:', error)
            callback([])
        },
    )
}
